import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Select from './Select';
import Modal from './ui/Modal';
import Icon from './ui/Icon';

const fmt = (v) => v?.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export default function ModalPlantarNaSafra({ safraId, safraNome, lavouras, safraLavouras, onConfirmar, onClose, erro }) {
  const [form, setForm] = useState({ lavouraId: '', variedade: '', areaHectares: '' });
  const [confirmar, setConfirmar] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const lavoura = lavouras.find(l => String(l.id) === String(form.lavouraId));
  const plantiosExistentes = safraLavouras.filter(sl => String(sl.lavoura?.id) === String(form.lavouraId));
  const areaJaPlantada = plantiosExistentes.reduce((s, sl) => s + (sl.areaHectares || 0), 0);
  const areaTotal = lavoura?.areaHectares || 0;
  const areaLivre = Math.max(0, areaTotal - areaJaPlantada);
  const jaPlantado = plantiosExistentes.length > 0;

  // A cultura vem da safra e o backend a grava em cada plantio, então os plantios
  // já existentes desta safra são a fonte confiável do nome (o form não tem esse campo).
  const cultura = plantiosExistentes[0]?.tipoGrao || safraLavouras[0]?.tipoGrao || null;

  const handleLavoura = (id) => {
    const l = lavouras.find(x => String(x.id) === String(id));
    setForm(f => ({ ...f, lavouraId: id, areaHectares: l?.areaHectares ?? '' }));
    setConfirmar(false);
  };

  const enviar = async () => {
    // Guarda contra duplo envio: em rede lenta, dois toques criavam dois plantios.
    if (salvando) return;
    setSalvando(true);
    try {
      await onConfirmar({
        safraId,
        lavouraId: form.lavouraId,
        variedade: form.variedade || null,
        areaHectares: form.areaHectares !== '' ? parseFloat(form.areaHectares) : null,
      });
    } finally {
      // Libera também quando falha, senão o botão ficava travado para sempre.
      setSalvando(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // O Enter no formulário também submete, por isso a guarda vem antes de tudo.
    if (salvando) return;
    if (jaPlantado && !confirmar) { setConfirmar(true); return; }
    enviar();
  };

  return (
    <Modal titulo="Plantar nesta safra" onClose={onClose}>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>Safra {safraNome}</div>

      {erro && <div className="msg msg-error" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      {lavouras.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1.5rem 1rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <div style={{ marginBottom: '0.75rem', color: 'var(--accent)' }} aria-hidden="true">
            <Icon nome="lavoura" tamanho={32} />
          </div>
          <p style={{ fontSize: '0.88rem', marginBottom: '0.75rem' }}>Você ainda não tem áreas de cultivo cadastradas.</p>
          <Link to="/lavouras" onClick={onClose} style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none', fontSize: '0.88rem' }}>
            Cadastrar uma área de cultivo
          </Link>
        </div>
      ) : confirmar ? (
        <div>
          <div className="aviso aviso--atencao" style={{ padding: '1rem', marginBottom: '1.25rem' }}>
            <div style={{ fontWeight: 700, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <Icon nome="alerta" tamanho={16} /> Este talhão já tem plantio nesta safra
            </div>
            <div style={{ fontSize: '0.88rem', lineHeight: 1.7 }}>
              <div><strong>{lavoura?.descricao}</strong> tem <strong>{fmt(areaTotal)} ha</strong> no total.</div>
              <div>Já plantado nesta safra: <strong>{fmt(areaJaPlantada)} ha</strong></div>
              <div>Área ainda disponível: <strong>{fmt(areaLivre)} ha</strong></div>
              {parseFloat(form.areaHectares) > areaLivre && areaLivre > 0 && (
                <div style={{ marginTop: '0.5rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Icon nome="alerta" tamanho={14} />
                  Você informou {fmt(parseFloat(form.areaHectares))} ha, mas só há {fmt(areaLivre)} ha disponíveis.
                </div>
              )}
            </div>
          </div>
          <div style={{ fontSize: '0.88rem', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
            Confirma o plantio de <strong>{form.areaHectares} ha</strong>
            {cultura ? <> de <strong>{cultura}</strong></> : null}
            {form.variedade ? ` (${form.variedade})` : ''} no mesmo talhão?
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button className="btn-secondary" onClick={() => setConfirmar(false)} disabled={salvando}>Voltar e corrigir</button>
            <button className="btn-primary" onClick={enviar} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Confirmar mesmo assim'}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', fontSize: '0.85rem' }}>
            <div>
              <label>Área de Cultivo</label>
              <Select
                value={form.lavouraId}
                onChange={handleLavoura}
                options={lavouras.map(l => ({ value: l.id, label: `${l.tipoArea === 'ESTUFA' ? 'Estufa' : 'Lavoura'} - ${l.descricao}${l.areaHectares ? ` (${fmt(l.areaHectares)} ${l.tipoArea === 'ESTUFA' ? 'm²' : 'ha'})` : ''}` }))}
                placeholder="Selecione a área"
                required
                style={{ fontSize: '0.82rem', padding: '0.4rem 0.6rem', minHeight: 'unset' }}
              />
            </div>
            <div>
                <label>Variedade <span style={{ fontWeight: 400, textTransform: 'none' }}>(opcional)</span></label>
                <input value={form.variedade} onChange={e => setForm(f => ({ ...f, variedade: e.target.value }))} placeholder="Ex: TMG 7062" style={{ fontSize: '0.82rem', padding: '0.4rem 0.6rem' }} />
              </div>
              <div>
                <label>Área plantada (ha)</label>
                <input
                  type="number" step="0.1" min="0.1"
                  value={form.areaHectares}
                  onChange={e => setForm(f => ({ ...f, areaHectares: e.target.value }))}
                  placeholder={lavoura ? `Máx. ${fmt(areaTotal)} ha` : 'Ex: 10.5'}
                  style={{ fontSize: '0.82rem', padding: '0.4rem 0.6rem' }}
                />
              </div>
            {lavoura && (
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'block' }}>
                Área total: <strong>{fmt(areaTotal)} ha</strong>
                {jaPlantado && <> · Já plantado: <strong>{fmt(areaJaPlantada)} ha</strong> · Disponível: <strong style={{ color: areaLivre > 0 ? '#276749' : '#e53e3e' }}>{fmt(areaLivre)} ha</strong></>}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={salvando}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={salvando || !form.lavouraId}>
              {salvando ? 'Salvando...' : 'Confirmar plantio'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
